import {
  articleDataSchema,
  eventDataSchema,
  organisationDataSchema,
  postalAddressDataSchema,
  productDataSchema,
  webPageDataSchema,
  webSiteDataSchema,
} from '@lib/structured-data/schema';
import {
  Blog,
  Events,
  News,
  Section,
  SettingsOrganisation,
  SettingsWebsite,
  SeoData,
  StructuredDataSection,
} from '@lib/types';
import {
  articleId,
  organisationId,
  productId,
  webSiteId,
} from '@lib/structured-data/ids';
import { buildImageUrl } from '@lib/image';
import Head from 'next/head';

type OrganisationJsonLdProps = {
  organisation: SettingsOrganisation | null;
  website: SettingsWebsite;
  sameAsUrls?: string[];
};

export function OrganisationJsonLd({
  organisation,
  website,
  sameAsUrls,
}: OrganisationJsonLdProps) {
  const sameAs = Array.from(new Set((sameAsUrls ?? []).filter(Boolean)));
  const address = postalAddressDataSchema.safeParse({
    '@type': 'PostalAddress',
    addressCountry: organisation?.country || undefined,
    addressLocality: organisation?.city || undefined,
    streetAddress: organisation?.street || undefined,
    extendedAddress: organisation?.extendedAddress || undefined,
    postalCode: organisation?.postalCode || undefined,
  });

  const organisationData = organisationDataSchema.safeParse({
    '@type': 'Organization',
    '@id': organisationId(website),
    name: organisation?.name,
    url: website.url,
    sameAs: sameAs.length ? sameAs : undefined,
    email: organisation?.email || undefined,
    address: address.success ? address.data : undefined,
  });

  if (!organisationData.success) return null;

  return <JsonLd id="organisation" data={organisationData.data} />;
}

type WebSiteJsonLdProps = {
  website: SettingsWebsite;
};

export function WebSiteJsonLd({ website }: WebSiteJsonLdProps) {
  const webSiteData = webSiteDataSchema.safeParse({
    '@type': 'WebSite',
    '@id': webSiteId(website),
    name: website.name,
    url: website.url,
    publisher: { '@id': organisationId(website) },
  });

  if (!webSiteData.success) return null;

  return <JsonLd id="website" data={webSiteData.data} />;
}

type WebPageJsonLdProps = {
  url: string;
  title: string;
  locale?: string;
  seo: SeoData | null;
  website: SettingsWebsite;
  structuredData?: StructuredDataSection[];
};

export function WebPageJsonLd({
  url,
  title,
  locale,
  seo,
  website,
  structuredData,
}: WebPageJsonLdProps) {
  const canonicalUrl = seo?.canonicalURL || url;
  const name = seo?.metaTitle || title;
  const description = seo?.metaDescription;
  const imageUrl = seo?.metaImageUrl ? buildImageUrl(seo.metaImageUrl) : undefined;
  const aboutIds = structuredData
    ? productAboutIds(structuredData, website)
    : [];

  const webPageData = webPageDataSchema.safeParse({
    '@type': 'WebPage',
    '@id': canonicalUrl,
    url: canonicalUrl,
    name,
    description,
    inLanguage: locale,
    isPartOf: { '@id': webSiteId(website) },
    primaryImageOfPage: imageUrl
      ? { '@type': 'ImageObject', url: imageUrl }
      : undefined,
    about: aboutIds.length
      ? aboutIds.map((id) => ({ '@id': id }))
      : undefined,
  });

  if (!webPageData.success) return null;

  return <JsonLd id="webpage" data={webPageData.data} />;
}

type NewsArticleJsonLdProps = {
  news: News;
  url: string;
  website: SettingsWebsite;
};

export function NewsArticleJsonLd({
  news,
  url,
  website,
}: NewsArticleJsonLdProps) {
  return articleJsonLd({
    type: 'NewsArticle',
    website,
    canonicalUrl: news.seo?.canonicalURL || url,
    headline: news.seo?.metaTitle || news.title,
    locale: news.locale ?? undefined,
    publishDate: news.publishDate,
    dateModified: news.updatedAt,
    imageUrl: news.image?.url || news.seo?.metaImageUrl,
    author: { '@id': organisationId(website) },
  });
}

type BlogPostingJsonLdProps = {
  blog: Blog;
  url: string;
  website: SettingsWebsite;
};

export function BlogPostingJsonLd({
  blog,
  url,
  website,
}: BlogPostingJsonLdProps) {
  return articleJsonLd({
    type: 'BlogPosting',
    website,
    canonicalUrl: blog.seo?.canonicalURL || url,
    headline: blog.seo?.metaTitle || blog.title,
    locale: blog.locale ?? undefined,
    publishDate: blog.publishDate,
    dateModified: blog.updatedAt,
    imageUrl: blog.image?.url || blog.seo?.metaImageUrl,
    author: blog.author?.name
      ? {
          '@type': 'Person',
          name: blog.author.name,
          jobTitle: blog.author.role || undefined,
        }
      : { '@id': organisationId(website) },
  });
}

type ProductsJsonLdProps = {
  structuredData: StructuredDataSection[];
  website: SettingsWebsite;
};

export function ProductsJsonLd({
  structuredData,
  website,
}: ProductsJsonLdProps) {
  const ownerId = organisationId(website);

  const products = productSections(structuredData)
    .map((product) =>
      productDataSchema.safeParse({
        '@type': ['Product', 'MedicalDevice'],
        '@id': productId(website, product.structuredDataId),
        name: product.name,
        description: product.description || undefined,
        image: product.imageUrl ? buildImageUrl(product.imageUrl) : undefined,
        owner: { '@id': ownerId },
        manufacturer: { '@id': ownerId },
      }),
    )
    .filter((r) => r.success)
    .map((r) => r.data!);

  return (
    <>
      {products.map((product, index) => (
        <JsonLd
          key={`product-${index}`}
          id={`product-${index}`}
          data={product}
        />
      ))}
    </>
  );
}

type EventsJsonLdProps = {
  sections: Section[];
};

export function EventsJsonLd({ sections }: EventsJsonLdProps) {
  const candidates = sections.flatMap((section) => {
    if (section.sectionName !== 'ComponentSectionsEvents') return [];
    const s = section as Events;
    return s.events.map((event) => ({
      name: event.title,
      startDate: event.start,
      endDate: event.end,
      description: stripHtml(event.description),
      location: event.location,
    }));
  });

  const events = candidates
    .map((c) =>
      eventDataSchema.safeParse({
        '@type': 'Event',
        name: c.name,
        startDate: c.startDate,
        endDate: c.endDate,
        description: c.description || undefined,
        location: c.location || undefined,
      }),
    )
    .filter((r) => r.success)
    .map((r) => r.data!);

  return (
    <>
      {events.map((event, index) => (
        <JsonLd key={`event-${index}`} id={`event-${index}`} data={event} />
      ))}
    </>
  );
}

type JsonLdProps<T> = {
  id: string;
  data: T;
};

function JsonLd<T extends { '@type': string | string[] }>({
  id,
  data,
}: JsonLdProps<T>) {
  const payload = serializeJsonLd({ '@context': 'https://schema.org', ...data });
  return (
    <Head>
      <script
        type="application/ld+json"
        key={id}
        dangerouslySetInnerHTML={{ __html: payload }}
      />
    </Head>
  );
}

type ArticleAuthor =
  | { '@type': 'Person'; name: string; jobTitle?: string }
  | { '@id': string };

type ArticleJsonLdArgs = {
  type: 'NewsArticle' | 'BlogPosting';
  canonicalUrl: string;
  headline: string;
  locale?: string;
  publishDate: string;
  dateModified?: string;
  imageUrl?: string;
  website: SettingsWebsite;
  author: ArticleAuthor;
};

function articleJsonLd({
  type,
  canonicalUrl,
  headline,
  locale,
  publishDate,
  dateModified,
  imageUrl,
  website,
  author,
}: ArticleJsonLdArgs) {
  const articleData = articleDataSchema.safeParse({
    '@type': type,
    '@id': articleId(canonicalUrl),
    headline,
    datePublished: publishDate,
    dateModified: dateModified || undefined,
    image: imageUrl ? buildImageUrl(imageUrl) : undefined,
    inLanguage: locale,
    mainEntityOfPage: { '@id': canonicalUrl },
    publisher: { '@id': organisationId(website) },
    author,
  });

  if (!articleData.success) return null;

  return <JsonLd id="article" data={articleData.data} />;
}

function productSections(structuredData: StructuredDataSection[]) {
  return structuredData.filter(
    (item) => item.componentName === 'ComponentSeoProduct',
  );
}

function productAboutIds(
  structuredData: StructuredDataSection[],
  website: SettingsWebsite,
): string[] {
  return productSections(structuredData).map((product) =>
    productId(website, product.structuredDataId),
  );
}

function stripHtml(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}
